import { useEffect, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useOrganization } from '../contexts/OrganizationContext'
import { creatorApi } from '../services/api'
import { Alert, Button, Dialog, Field, FileUploadField, Input } from './common'
import styles from './CreatorCreateDialog.module.css'

// allowAvatar offers the optional profile image. It follows the avatar
// permission (useCanManageCreators), which an editor adding a creator lacks.
function CreatorCreateDialog({ open, onClose, onCreated, allowAvatar = true }) {
  const { currentOrg } = useOrganization()
  const sequenceRef = useRef(0)
  const activeRequestRef = useRef(0)
  const [displayName, setDisplayName] = useState('')
  const [avatarFile, setAvatarFile] = useState(null)
  const [avatarPreview, setAvatarPreview] = useState(null)
  const [avatarUploading, setAvatarUploading] = useState(false)
  const [error, setError] = useState('')

  const resetFields = () => {
    if (avatarFile && avatarPreview) URL.revokeObjectURL(avatarPreview)
    setDisplayName('')
    setAvatarFile(null)
    setAvatarPreview(null)
    setAvatarUploading(false)
    setError('')
  }

  useEffect(() => {
    activeRequestRef.current = 0
    if (open) setError('')
    return () => {
      activeRequestRef.current = 0
    }
  }, [open])

  const closeDialog = () => {
    activeRequestRef.current = 0
    resetFields()
    onClose?.()
  }

  const addMutation = useMutation({
    mutationFn: ({ displayName: name }) => creatorApi.add(currentOrg.slug, name),
    onMutate: () => {
      const token = ++sequenceRef.current
      activeRequestRef.current = token
      return { token }
    },
    onSuccess: async (data, _variables, context) => {
      const active = context?.token === activeRequestRef.current
      if (!data?.creator) {
        if (active) setError('Creator was created but the server returned no creator')
        return
      }

      let uploadedUrl = ''
      let avatarUploadFailed = false
      if (active && avatarFile && data.creator.id) {
        setAvatarUploading(true)
        try {
          const uploadResult = await creatorApi.uploadAvatar(currentOrg.slug, data.creator.id, avatarFile)
          uploadedUrl = uploadResult?.avatar_url || ''
        } catch {
          avatarUploadFailed = true
        } finally {
          setAvatarUploading(false)
        }
      }

      const selected = context?.token === activeRequestRef.current
      const creator = {
        ...data.creator,
        avatar_url: uploadedUrl || data.creator.avatar_url || '',
      }
      onCreated?.(creator, { avatarUploadFailed, selected })
      if (selected) {
        resetFields()
        onClose?.()
      }
    },
    onError: (err, _variables, context) => {
      if (context?.token === activeRequestRef.current) {
        setError(err.response?.data?.error || err.message || 'Failed to add creator')
      }
    },
  })

  const handleAvatarFileChange = (event) => {
    const file = event.target.files?.[0]
    if (!file) return

    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
    if (!allowed.includes(file.type)) {
      setError('Only JPEG, PNG, WebP, and GIF images are allowed')
      return
    }

    if (avatarFile && avatarPreview) URL.revokeObjectURL(avatarPreview)
    setAvatarFile(file)
    setAvatarPreview(URL.createObjectURL(file))
    event.target.value = ''
  }

  const handleRemoveAvatar = () => {
    if (avatarFile && avatarPreview) URL.revokeObjectURL(avatarPreview)
    setAvatarFile(null)
    setAvatarPreview(null)
  }

  const isAdding = addMutation.isPending || avatarUploading

  return (
    <Dialog
      open={open}
      title="Add creator"
      description={allowAvatar
        ? 'Add a creator to this organization and optionally upload a profile image now.'
        : 'Add a creator to this organization.'}
      onClose={closeDialog}
      footer={(
        <>
          <Button variant="secondary" onClick={closeDialog}>Cancel</Button>
          <Button
            onClick={() => addMutation.mutate({ displayName: displayName.trim() })}
            disabled={!displayName.trim() || isAdding}
          >
            {isAdding ? 'Creating...' : 'Create'}
          </Button>
        </>
      )}
    >
      <div className={styles.modalStack}>
        {error ? <Alert variant="error">{error}</Alert> : null}
        {allowAvatar ? (
          <FileUploadField
            label="Avatar"
            variant="avatar"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={handleAvatarFileChange}
            triggerLabel="Choose Image"
            filename={avatarFile?.name}
            previewSrc={avatarPreview}
            placeholderText={(displayName || '?').charAt(0).toUpperCase()}
            onClear={avatarPreview ? handleRemoveAvatar : undefined}
            helper="JPEG, PNG, WebP, and GIF files are supported."
            pending={avatarUploading}
          />
        ) : null}
        <Field label="Display name" required>
          <Input
            value={displayName}
            onChange={(event) => {
              setDisplayName(event.target.value)
              if (error) setError('')
            }}
            placeholder="Enter display name"
            data-autofocus
          />
        </Field>
      </div>
    </Dialog>
  )
}

export default CreatorCreateDialog
